/**
 * Normalize error payloads from job stream / API (often nested JSON strings).
 */

const DEFAULT_MESSAGE = 'Document processing failed';

const INCOMPLETE_DECLARATION_DEFAULT =
  'No complete declaration could be extracted from the provided documents.';

/**
 * Pull a user-facing message from model text when extraction ended without results_ready
 * (e.g. markdown JSON plus "CRITICAL FIELDS MISSING" warnings).
 *
 * @param {string|null|undefined} text
 * @returns {string|null}
 */
export function extractIncompleteDeclarationError(text) {
  if (!text || typeof text !== 'string') {
    return null;
  }

  const normalized = text.trim();
  if (!normalized) {
    return null;
  }

  const hasCriticalFields = /CRITICAL\s+FIELDS\s+MISSING/i.test(normalized);
  const hasIncompleteDeclaration = /Declaration is incomplete/i.test(normalized);

  if (!hasCriticalFields && !hasIncompleteDeclaration) {
    return null;
  }

  const startIdx = hasCriticalFields
    ? normalized.search(/CRITICAL\s+FIELDS\s+MISSING/i)
    : normalized.search(/Declaration is incomplete/i);

  let excerpt = normalized.slice(Math.max(0, startIdx), startIdx + 1400);

  const cleaned = excerpt
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^>\s?/gm, '')
    .replace(/\*\*/g, '')
    .replace(/\|/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (cleaned.length < 24) {
    return INCOMPLETE_DECLARATION_DEFAULT;
  }

  return cleaned;
}

/**
 * @param {{ resultsReceived?: boolean, accumulatedText?: string, stage?: string }} params
 * @returns {string|null}
 */
export function getIncompleteStreamFailureMessage({
  resultsReceived,
  accumulatedText,
  stage,
} = {}) {
  if (resultsReceived) {
    return null;
  }

  const fromText = extractIncompleteDeclarationError(accumulatedText);
  if (fromText) {
    return fromText;
  }

  if (stage === 'final_message_received') {
    return INCOMPLETE_DECLARATION_DEFAULT;
  }

  return null;
}

/**
 * @param {unknown} raw
 * @returns {string}
 */
export function parseProcessingErrorMessage(raw) {
  if (raw == null || raw === '') {
    return DEFAULT_MESSAGE;
  }

  if (typeof raw === 'object') {
    return (
      raw?.error?.message ||
      raw?.message ||
      raw?.error ||
      raw?.detail ||
      DEFAULT_MESSAGE
    );
  }

  if (typeof raw !== 'string') {
    return String(raw);
  }

  const trimmed = raw.trim();
  if (!trimmed) {
    return DEFAULT_MESSAGE;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      return (
        parsed?.error?.message ||
        parsed?.message ||
        (typeof parsed?.error === 'string' ? parsed.error : null) ||
        parseProcessingErrorMessage(parsed.error) ||
        trimmed
      );
    }
  } catch {
    // not JSON — use as plain text
  }

  return trimmed;
}

/**
 * @param {object|null|undefined} status
 * @returns {string|null} User-facing message when status represents a failure
 */
export function getStreamFailureMessage(status) {
  if (!status || typeof status !== 'object') {
    return null;
  }

  const isFailedStatus = status.status === 'failed' || status.status === 'error';
  const isStreamFailureStage =
    status.stage === 'stream_error' ||
    status.stage === 'stream_failed' ||
    status.stage === 'incomplete_extraction';

  if (!isFailedStatus && !isStreamFailureStage) {
    return null;
  }

  return parseProcessingErrorMessage(
    status.error ?? status.message ?? status.detail
  );
}
